import { head, get } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';

import {
  connectCreateOfferFormQuery,
} from './offer_operations';

import {
  Listing,
  core_apimessages_Channel,
} from '../rql';
import { connect } from '../connect';

interface IConnectedProps {
  isDisplayed: boolean; // Used for skipping initial gql query
  listingId: string;
  isSeller: boolean;
  shopChannel: core_apimessages_Channel;
  listingUrlGenerator: (id: string) => string;
}

interface IProps {
  listing: Listing;
  isLoading: boolean;
  isSeller: boolean;
  listingUrlGenerator: (id: string) => string;
}

const EMPTY_CELL_TEXT = '--';

function renderImage(listing: Listing) {
  const imageUrl = get(head(listing?.images), 'source');

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={listing.title}
      />
    );
  }

  return null;
}

export function OfferListingSidebar({ listing, isSeller, isLoading, listingUrlGenerator }: IProps) {
  if (isLoading) {
    return (
      <div className="modal-sidebar-listing modal-sidebar-listing--loading">
        <div className="modal-sidebar-listing__thumbnail" />
        <div className="modal-sidebar-listing__title" />
        <div className="modal-sidebar-listing__details-label" />
        <div className="modal-sidebar-listing__details" />
        <div className="modal-sidebar-listing__link" />
      </div>
    );
  }

  const condition = get(listing, 'condition.displayName');

  const sellerCost = listing?.sellerCost?.display;
  const displayPrice = listing?.price?.display;
  const sellerDisplayPrice = listing?.sellerPrice?.display;
  const askingPrice = isSeller ? sellerDisplayPrice : displayPrice;

  // The seller's price won't include VAT. If tax is present, we display it as an additional "list price" field.
  const shouldDisplayListPriceWithTaxIncluded = isSeller && listing?.taxIncluded && listing?.taxIncludedHint;
  const shouldDisplaySellerCost = isSeller && !!sellerCost;

  return (
    <div className="modal-sidebar-listing">
      <div className="modal-sidebar-listing__thumbnail">
        {renderImage(listing)}
      </div>

      <h2 className="modal-sidebar-listing__title">
        {listing?.title}
      </h2>

      <dl className="modal-sidebar-listing__details">
        {condition &&
          <div className="modal-sidebar-listing__detail">
            <dt>{I18n.t('commons.offers.sidebar.condition')}</dt>
            <dd>{condition}</dd>
          </div>
        }
        {shouldDisplaySellerCost && (
          <div className="modal-sidebar-listing__detail">
            <dt>{I18n.t('commons.offers.sidebar.sellerCost')}</dt>
            <dd>{sellerCost}</dd>
          </div>
        )}
        <div className="modal-sidebar-listing__detail">
          <dt>{I18n.t('commons.offers.sidebar.askingPrice')}</dt>
          <dd>{askingPrice}</dd>
        </div>
        {shouldDisplayListPriceWithTaxIncluded && (
          <div className="modal-sidebar-listing__detail">
            <dt>{I18n.t('commons.offers.sidebar.listPrice')}</dt>
            <dd>
              {
                I18n.t('commons.offers.sidebar.priceWithTaxIncludedHint', {
                  displayPrice,
                  taxIncludedHint: listing.taxIncludedHint,
                })
              }
            </dd>
          </div>
        )}
        <div className="modal-sidebar-listing__detail">
          <dt>{I18n.t('commons.offers.sidebar.shopDisplayLocation')}</dt>
          <dd>{get(listing, 'shop.address.displayLocation', EMPTY_CELL_TEXT)}</dd>
        </div>
      </dl>

      {listing && listingUrlGenerator &&
        <a
          href={listingUrlGenerator(listing.id)}
          className="modal-sidebar-listing__link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {I18n.t('commons.offers.sidebar.viewListing')}
        </a>
      }
    </div>
  );
}

export const ConnectedOfferListingSidebar = connect<IConnectedProps>([
  connectCreateOfferFormQuery,
])((props) => {
  if (!props.isDisplayed) { return null; }

  return (
    <OfferListingSidebar
      {...props}
      isLoading={get(props, 'data.loading')}
      listing={get(props, 'data.listing')}
    />
  );
});
